import React, { useState } from "react";
import {
    IResourceComponentsProps,
    useNavigation,
    useCustom,
} from "@pankod/refine-core";
import {
    List,
    Stack,
    Typography,
    Grid,
    Card,
    Button,
    TextField,
    Box,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
} from "@pankod/refine-mui";
import { 
    Print, 
    Visibility, 
    Download, 
    FilterList,
    Refresh,
    School,
    People,
    Book,
    Quiz
} from "@mui/icons-material";

interface ClassSummaryData {
    studentName: string;
    bookTitle: string;
    dateBookCompleted: string | null;
    quizScore: number | null;
    gradeLevelOfBook: number;
    bookCompleted: boolean;
    quizPassed: boolean;
    quizCompleted: boolean;
}

interface ClassInfo {
    name: string;
    dateRange: string;
    totalStudents: number;
    totalBooksRead: number;
    booksCompleted: number;
    averageQuizScore: number;
}

interface ClassSummaryResponse {
    classInfo: ClassInfo;
    students: ClassSummaryData[];
}

export const ClassSummaryReportList: React.FC<IResourceComponentsProps> = () => {
    const [selectedGrade, setSelectedGrade] = useState<string>("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [reportData, setReportData] = useState<ClassSummaryResponse | null>(null);
    const [error, setError] = useState<string | null>(null);

    const { show } = useNavigation();

    const handleGenerateReport = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            const token = localStorage.getItem("token");
            const params = new URLSearchParams();
            
            if (selectedGrade) params.append('grade', selectedGrade);
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            
            const url = `${process.env.REACT_APP_BACKEND}admin/libraryReport/class-summary?${params.toString()}`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.success) {
                setReportData(result.data);
            } else {
                throw new Error(result.error || 'Failed to generate report');
            }
        } catch (err) {
            console.error('Error generating report:', err);
            setError(err instanceof Error ? err.message : 'Failed to generate report');
        } finally {
            setIsLoading(false);
        }
    };

    const handleExportCSV = async () => {
        try {
            const token = localStorage.getItem("token");
            const params = new URLSearchParams();
            
            if (selectedGrade) params.append('grade', selectedGrade);
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            
            const url = `${process.env.REACT_APP_BACKEND}admin/libraryReport/class-summary/export?${params.toString()}`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const csvData = await response.text();
            const blob = new Blob([csvData], { type: 'text/csv' });
            const url2 = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url2;
            link.download = `class-summary-report-${new Date().toISOString().split('T')[0]}.csv`;
            link.click();
            window.URL.revokeObjectURL(url2);
        } catch (err) {
            console.error('Error exporting CSV:', err);
            alert('Failed to export CSV. Please try again.');
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "Not Completed";
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const getScoreColor = (score: number | null) => {
        if (score === null) return "default";
        if (score >= 90) return "success";
        if (score >= 80) return "info";
        if (score >= 60) return "warning";
        return "error";
    };

    const getScoreLabel = (score: number | null) => {
        if (score === null) return "N/A";
        if (score >= 90) return "Excellent";
        if (score >= 80) return "Good";
        if (score >= 60) return "Fair";
        return "Needs Improvement";
    };

    return (
        <List
            title="Library Report"
            headerButtons={
                <Box>
                    <Typography variant="body2" color="textSecondary">
                        Generate comprehensive class reading progress reports
                    </Typography>
                </Box>
            }
        >
            <Grid container spacing={3}>
                {/* Filters */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Report Filters
                        </Typography>
                        
                        <Stack spacing={3}>
                            <FormControl fullWidth>
                                <InputLabel>Grade Level</InputLabel>
                                <Select
                                    value={selectedGrade}
                                    onChange={(e) => setSelectedGrade(e.target.value)}
                                    label="Grade Level"
                                >
                                    <MenuItem value="">All Grades</MenuItem>
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map(grade => (
                                        <MenuItem key={grade} value={grade.toString()}>
                                            Grade {grade}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                label="Start Date"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />

                            <TextField
                                fullWidth
                                label="End Date"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />

                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="contained"
                                    onClick={handleGenerateReport}
                                    disabled={isLoading}
                                    startIcon={isLoading ? <CircularProgress size={20} /> : <Refresh />}
                                    fullWidth
                                >
                                    {isLoading ? 'Generating...' : 'Generate Report'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

                {/* Report Results */}
                <Grid item xs={12} md={8}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {reportData && (
                        <Paper sx={{ p: 3 }}>
                            {/* Class Summary Info */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h5" gutterBottom>
                                    {reportData.classInfo.name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    Date Range: {reportData.classInfo.dateRange}
                                </Typography>
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={6} md={3}>
                                        <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
                                            <Typography variant="h4" color="primary">
                                                {reportData.classInfo.totalStudents}
                                            </Typography>
                                            <Typography variant="body2">Total Students</Typography>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
                                            <Typography variant="h4" color="info.main">
                                                {reportData.classInfo.totalBooksRead}
                                            </Typography>
                                            <Typography variant="body2">Books Read</Typography>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                                            <Typography variant="h4" color="success.main">
                                                {reportData.classInfo.booksCompleted}
                                            </Typography>
                                            <Typography variant="body2">Books Completed</Typography>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
                                            <Typography variant="h4" color="warning.main">
                                                {reportData.classInfo.averageQuizScore}%
                                            </Typography>
                                            <Typography variant="body2">Avg Quiz Score</Typography>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Action Buttons */}
                            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<Download />}
                                    onClick={handleExportCSV}
                                    color="success"
                                >
                                    Export CSV
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<Print />}
                                    onClick={() => window.print()}
                                >
                                    Print Report
                                </Button>
                            </Stack>

                            {/* Student Data Table */}
                            <Typography variant="h6" gutterBottom>
                                <People sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Student Reading Progress
                            </Typography>
                            
                            {reportData.students.length === 0 ? (
                                <Alert severity="info">
                                    No student data found for the selected criteria.
                                </Alert>
                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>Student Name</strong></TableCell>
                                                <TableCell><strong>Book Title</strong></TableCell>
                                                <TableCell align="center"><strong>Date Completed</strong></TableCell>
                                                <TableCell align="center"><strong>Quiz Score</strong></TableCell>
                                                <TableCell align="center"><strong>Grade Level</strong></TableCell>
                                                <TableCell align="center"><strong>Status</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {reportData.students.map((student, index) => (
                                                <TableRow key={`${student.studentName}-${student.bookTitle}-${index}`} hover>
                                                    <TableCell>
                                                        <Typography variant="subtitle2" fontWeight="500">
                                                            {student.studentName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {student.bookTitle}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2">
                                                            {formatDate(student.dateBookCompleted)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {student.quizScore !== null ? (
                                                            <Chip
                                                                label={`${student.quizScore}%`}
                                                                color={getScoreColor(student.quizScore)}
                                                                size="small"
                                                                sx={{ fontWeight: 'bold' }}
                                                            />
                                                        ) : (
                                                            <Typography variant="caption" color="textSecondary">
                                                                N/A
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={`Grade ${student.gradeLevelOfBook}`}
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Stack direction="row" spacing={1} justifyContent="center">
                                                            <Chip
                                                                label={student.bookCompleted ? "✓ Book" : "✗ Book"}
                                                                color={student.bookCompleted ? "success" : "error"}
                                                                size="small"
                                                            />
                                                            <Chip
                                                                label={student.quizPassed ? "✓ Quiz" : "✗ Quiz"}
                                                                color={student.quizPassed ? "success" : "error"}
                                                                size="small"
                                                            />
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    )}

                    {!reportData && !isLoading && !error && (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <Book sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="textSecondary" gutterBottom>
                                No Report Generated
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Select your filters and click "Generate Report" to view class reading progress.
                            </Typography>
                        </Paper>
                    )}
                </Grid>
            </Grid>
        </List>
    );
};
